import React, { useState, useMemo } from "react";
import VerificationCode from "../../components/common/VerificationCode";
import { useTheme, useMediaQuery } from "@mui/material";
import { Images } from "../../utils/Images";
import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import BannerSection from "../../components/common/BannerSection";
import NewsLogoSection from "../HomePage/NewsLogoSection";
import { useDispatch, useSelector } from "react-redux";
import { startNewCampaignAction } from "../../store/slices/campaignSlice";
import { sendOTP, verifyOTP } from "../../api/services/authService";
import { useNavigate, Link } from "react-router-dom";
import {
  sanitizeIndianMobileInput,
  validateIndianMobile,
} from "../../utils/phoneValidation";

const StartNewCampaign = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isAbove1200 = useMediaQuery("(min-width: 1200px)");
  const isBelow900 = useMediaQuery("(max-width: 899px)");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, startCampaignResult, error } = useSelector((state) => state.campaign);
  const home = useSelector((state) => state.cms.home);
  const cmsImage = useMemo(() => {
    const sections = Array.isArray(home?.data) ? home.data : [];
    const section = sections.find((s) => s?.id === 80);
    return section?.image || "";
  }, [home]);

  const [socialLinks, setSocialLinks] = useState([""]);
  const [showVerification, setShowVerification] = useState(false);
  const [emailForOTP, setEmailForOTP] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const [showRetryDialog, setShowRetryDialog] = useState(false);

  // Form fields
  const [socialCause, setSocialCause] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [description, setDescription] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState({
    socialCause: "",
    campaignName: "",
    email: "",
    phone: "",
    description: "",
    termsAccepted: "",
  });

  // Categories from explore campaigns
  const categories = [
    "Urgent",
    "Children",
    "Animals",
    "Disability",
    "Disaster Relief",
    "Education",
    "Elderly",
    "Faith",
    "Hunger",
    "Medical",
    "Diy",
    "Women",
    "Temples",
    "Community",
  ];

  const handleAddLink = () => setSocialLinks([...socialLinks, ""]);
  const handleLinkChange = (idx, value) => {
    const updated = [...socialLinks];
    updated[idx] = value;
    setSocialLinks(updated);
  };
  const handleDeleteLink = (idx) => {
    if (socialLinks.length > 1) {
      const updated = socialLinks.filter((_, index) => index !== idx);
      setSocialLinks(updated);
    }
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {
      socialCause: "",
      campaignName: "",
      email: "",
      phone: "",
      description: "",
      termsAccepted: "",
    };
    let isValid = true;

    // Validate social cause
    if (!socialCause || socialCause.trim() === "") {
      newErrors.socialCause = "Please select a social cause";
      isValid = false;
    }

    // Validate campaign name
    if (!campaignName || campaignName.trim() === "") {
      newErrors.campaignName = "Campaigner name is required";
      isValid = false;
    }

    // Validate email
    if (!email || email.trim() === "") {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!email.includes("@") || !email.includes(".")) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    const phoneCheck = validateIndianMobile(phone);
    if (!phoneCheck.isValid) {
      newErrors.phone = phoneCheck.error;
      isValid = false;
    }

    // Validate description
    if (!description || description.trim() === "") {
      newErrors.description = "Description is required";
      isValid = false;
    }

    // Validate terms checkbox
    if (!termsAccepted) {
      newErrors.termsAccepted = "You must accept the Terms of Use and Privacy Policy";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    // Clear previous errors
    setOtpError(null);
    setErrors({
      socialCause: "",
      campaignName: "",
      email: "",
      phone: "",
      description: "",
    });

    // Validate all fields
    if (!validateForm()) {
      return;
    }

    try {
      setOtpLoading(true);
      setOtpError(null);
      // Send OTP to email
      await sendOTP(email);
      setEmailForOTP(email);
      setShowVerification(true);
    } catch (error) {
      console.error("Error sending OTP:", error);
      setOtpError(error?.response?.data?.message || "Failed to send OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleCloseVerification = () => {
    setShowVerification(false);
    setOtpError(null);
    // Just close the popup, don't navigate - allows user to edit email
  };

  const handleVerifyCode = async (code) => {
    if (!emailForOTP || !code) return;
    
    try {
      setOtpLoading(true);
      setOtpError(null);
      // Verify OTP
      await verifyOTP(emailForOTP, code);
      // OTP verified successfully - automatically start campaign
      await handleVerificationSuccess();
      // Return success to show success screen
      return true;
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setOtpError(error?.response?.data?.message || "Invalid OTP. Please try again.");
      // Close verification and show retry dialog so user can start over
      setShowVerification(false);
      setShowRetryDialog(true);
      throw error; // Let child handle clearing inputs
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!emailForOTP) return;
    
    try {
      // Don't set otpLoading for resend - VerificationCode manages its own resend loading state
      setOtpError(null);
      await sendOTP(emailForOTP);
    } catch (error) {
      console.error("Error resending OTP:", error);
      setOtpError(error?.response?.data?.message || "Failed to resend OTP. Please try again.");
    }
  };

  const handleVerificationSuccess = async () => {
    const payload = {
      social_cause: socialCause,
      campaigner_name: campaignName,
      email,
      phone: validateIndianMobile(phone).digits,
      social_link: socialLinks.filter((l) => !!l && l.trim().length > 0),
      description,
      // status removed per backend requirement
    };

    try {
      const result = await dispatch(startNewCampaignAction(payload)).unwrap();
      console.log("Start campaign result:", result);
      // Don't close modal here - let success screen show.
      // Auto-close after 10 seconds and navigate to home.
      setTimeout(() => {
        setShowVerification(false);
        setSocialCause("");
        setCampaignName("");
        setEmail("");
        setPhone("");
        setDescription("");
        setSocialLinks([""]);
        setOtpError(null);
        // Navigate to home page
        navigate("/");
      }, 10000); // Auto-close after 10 seconds
    } catch (err) {
      console.error("Failed to start campaign:", err);
      setOtpError(err?.message || "Failed to start campaign. Please try again.");
      // Close modal and show retry dialog so user can start over
      setShowVerification(false);
      setShowRetryDialog(true);
    }
  };

  const handleSuccessPopupClose = () => {
    // When user closes the success popup manually, navigate to home immediately
    navigate("/");
  };

  const handleRetryReset = () => {
    // Reset entire form to empty state
    setSocialCause("");
    setCampaignName("");
    setEmail("");
    setPhone("");
    setDescription("");
    setSocialLinks([""]);
    setTermsAccepted(false);
    setErrors({
      socialCause: "",
      campaignName: "",
      email: "",
      phone: "",
      description: "",
      termsAccepted: "",
    });
    setOtpError(null);
    setEmailForOTP("");
    setShowVerification(false);
    setShowRetryDialog(false);
  };

  return (
    <Box>
      {/* ✅ Fixed closing tag */}
      <Box sx={{ mb: isMobile ? "10px" : "40px", mt: isMobile ? "0" : "30px" }}>
        <BannerSection
          title="Start New Campaign"
          subtitle="Your Action Can Make A Big Impact"
        />
      </Box>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: isBelow900 ? "none" : "#FAFAFA",
          padding: { xs: "0px", sm: "24px", md: "30px" },
          boxSizing: "border-box",
          width: "94.44%",
          maxWidth: "1440px",
          margin: "0 auto",
          borderRadius: "32px",
          mt: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "stretch",
            justifyContent: "center",
            height: "100%",
          }}
        >
          {/* Left Section - Image */}
          {!isBelow900 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "400px",
                mb: { xs: 4, md: 0 },
                marginRight: { sm: 1, md: 3, lg: 6 },
              }}
            >
              <Box
                sx={{
                  width: { xs: "300px", sm: "400px", md: "520px" },
                  height: { xs: "300px", sm: "400px", md: "528px" },
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {/* Background Mask Layer */}
                <Box
                  component="img"
                  src={Images.maskRedBox}
                  alt=""
                  sx={{
                    position: "absolute",
                    width: "80%",
                    height: "80%",
                    zIndex: 1,
                  }}
                />
                {/* Main Image with mask — CMS only */}
                {cmsImage ? (
                <Box
                  component="img"
                  src={cmsImage}
                  alt="Start New Campaign"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    maskImage: `url(${Images.monthlyMaskDesign})`,
                    maskSize: "100% 100%",
                    maskRepeat: "no-repeat",
                    maskPosition: "center",
                    WebkitMaskImage: `url(${Images.monthlyMaskDesign})`,
                    WebkitMaskSize: "100% 100%",
                    WebkitMaskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                    zIndex: 2,
                  }}
                />
                ) : null}
              </Box>
            </Box>
          )}
          {/* Right Section - Form */}
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: 4,
              paddingX: isMobile ? "15px" : "30px",
              paddingY: isMobile ? "20px" : "40px",
              maxWidth: isBelow900 ? "100%" : "600px",
              width: "100%",
              mx: { xs: "auto", md: 0 },
              my: { xs: 2, md: 0 },
              boxShadow: 3,
              position: "relative",
              minWidth: { xs: 0, md: 400 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="h5"
              fontWeight={700}
              fontSize={isMobile ? "14px" : isAbove1200 ? "22px" : "18px"}
              mb={1}
              pb={1}
              color="#1D1D1B"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <img
                src={Images.streamlineSharpStar}
                alt="star"
                style={{ width: 24, height: 24, marginRight: 8 }}
              />
              I am raising funds for:
            </Typography>
            <Box sx={{ borderBottom: "1px solid #e0e0e0", mb: 3 }} />

            {/* Choose social cause */}
            <Box sx={{ mb: 3 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 12,
                  fontWeight: 600,
                  fontSize: isMobile ? "14px" : isAbove1200 ? "16px" : "15px",
                  color: "#141414",
                }}
              >
                Choose social cause
              </label>
              <TextField
                select
                fullWidth
                value={socialCause}
                onChange={(e) => {
                  setSocialCause(e.target.value);
                  setErrors({ ...errors, socialCause: "" });
                }}
                variant="outlined"
                error={!!errors.socialCause}
                sx={{
                  borderRadius: 1,
                  borderColor: errors.socialCause ? "#F04951" : "#DDE2E5",
                  "& .MuiOutlinedInput-root": {
                    "& input": {
                      padding: "12px 14px",
                    },
                    "& .MuiSelect-select": {
                      padding: "12px 14px",
                    },
                  },
                }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="" disabled>
                  <span style={{ color: "#bdbdbd" }}>Select Cause</span>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
              {errors.socialCause && (
                <Typography sx={{ color: "#F04951", fontSize: "12px", mt: 0.5 }}>
                  {errors.socialCause}
                </Typography>
              )}
            </Box>

            {/* Campaigner Name */}
            <Box sx={{ mb: 3 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 12,
                  fontWeight: 600,
                  fontSize: isMobile ? "14px" : isAbove1200 ? "16px" : "15px",
                  color: "#141414",
                }}
              >
                Campaigner Name
              </label>
              <TextField
                fullWidth
                placeholder="Enter Campaigner Name"
                variant="outlined"
                value={campaignName}
                onChange={(e) => {
                  setCampaignName(e.target.value);
                  setErrors({ ...errors, campaignName: "" });
                }}
                error={!!errors.campaignName}
                sx={{
                  borderRadius: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    borderColor: errors.campaignName ? "#F04951" : "#DDE2E5",
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "12px 14px", // ✅ inner padding (top/bottom , left/right)
                  },
                }}
              />
              {errors.campaignName && (
                <Typography sx={{ color: "#F04951", fontSize: "12px", mt: 0.5 }}>
                  {errors.campaignName}
                </Typography>
              )}
            </Box>

            {/* Email & Phone */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 3,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: 12,
                    fontWeight: 600,
                    fontSize: isMobile ? "14px" : isAbove1200 ? "16px" : "15px",
                    color: "#141414",
                  }}
                >
                  Email ID
                </label>
                <TextField
                  fullWidth
                  placeholder="Enter Email ID"
                  variant="outlined"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrors({ ...errors, email: "" });
                  }}
                  error={!!errors.email}
                  sx={{
                    borderRadius: 1,
                    borderColor: errors.email ? "#F04951" : "#DDE2E5",
                    "& .MuiOutlinedInput-input": {
                      padding: "12px 14px", // ✅ controls the input padding
                    },
                  }}
                />
                {errors.email && (
                  <Typography sx={{ color: "#F04951", fontSize: "12px", mt: 0.5 }}>
                    {errors.email}
                  </Typography>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: 12,
                    fontWeight: 600,
                    fontSize: isMobile ? "14px" : isAbove1200 ? "16px" : "15px",
                    color: "#141414",
                  }}
                >
                  Phone Number
                </label>
                <TextField
                  fullWidth
                  placeholder="Enter Phone Number"
                  variant="outlined"
                  value={phone}
                  onChange={(e) => {
                    setPhone(sanitizeIndianMobileInput(e.target.value));
                    setErrors({ ...errors, phone: "" });
                  }}
                  type="tel"
                  inputProps={{ inputMode: "numeric", maxLength: 10 }}
                  error={!!errors.phone}
                  sx={{
                    borderRadius: 1,
                    borderColor: errors.phone ? "#F04951" : "#DDE2E5",
                    "& .MuiOutlinedInput-input": {
                      padding: "12px 14px", // ✅ controls the input padding
                    },
                  }}
                />
                {errors.phone && (
                  <Typography sx={{ color: "#F04951", fontSize: "12px", mt: 0.5 }}>
                    {errors.phone}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Social Links */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: isMobile ? "14px" : isAbove1200 ? "16px" : "15px",
                    color: "#141414",
                  }}
                >
                  Social Links{" "}
                  <span style={{ color: "#686866", fontWeight: 600 }}>
                    (Optional)
                  </span>
                </span>
                <Button
                  onClick={handleAddLink}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    color: "#222",
                    fontSize: isMobile ? "14px" : isAbove1200 ? "16px" : "15px",
                    p: 0,
                    minWidth: "73px",
                  }}
                  disableRipple
                >
                  + Add Link
                </Button>
              </Box>
              {socialLinks.map((link, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: idx < socialLinks.length - 1 ? 2 : 0,
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder="Enter Social Links"
                    variant="outlined"
                    value={link}
                    onChange={(e) => handleLinkChange(idx, e.target.value)}
                    sx={{
                      borderRadius: 1,
                      borderColor: "#DDE2E5",
                      "& .MuiOutlinedInput-input": {
                        padding: "12px 14px", // ✅ controls the input padding
                      },
                    }}
                  />
                  {idx > 0 && (
                    <Button
                      onClick={() => handleDeleteLink(idx)}
                      sx={{
                        minWidth: "auto",
                        width: "40px",
                        height: "40px",
                        p: 0,
                        borderRadius: 1,
                        border: "1px solid #DDE2E5",
                        backgroundColor: "#fff",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                      disableRipple
                    >
                      <img
                        src={Images.closeIcon}
                        alt="Delete"
                        style={{ width: 16, height: 16 }}
                      />
                    </Button>
                  )}
                </Box>
              ))}
            </Box>

            {/* Description */}
            <Box sx={{ mb: 3 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 12,
                  fontWeight: 600,
                  fontSize: isMobile ? "14px" : isAbove1200 ? "16px" : "15px",
                  color: "#141414",
                }}
              >
                Description
              </label>
              <TextField
                fullWidth
                placeholder="Enter Description"
                multiline
                rows={4}
                variant="outlined"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setErrors({ ...errors, description: "" });
                }}
                error={!!errors.description}
                sx={{
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    borderColor: errors.description ? "#F04951" : "#DDE2E5",
                  },
                }}
              />
              {errors.description && (
                <Typography sx={{ color: "#F04951", fontSize: "12px", mt: 0.5 }}>
                  {errors.description}
                </Typography>
              )}
            </Box>

            {/* Checkbox Terms */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <Checkbox 
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked);
                    setErrors({ ...errors, termsAccepted: "" });
                  }}
                  sx={{ p: 0.5, mr: isMobile ? 0.5 : 1, mt: isMobile ? -0.7 : -0.6 }}
                />
                <span style={{ 
                fontSize: isMobile ? "10px" : "14px", 
                color: "#1D1D1B",
                fontWeight: 500,
                fontStyle: "normal",
                lineHeight: isMobile ? "16px" : "20px",
                letterSpacing: "-0.04em"
              }}>
                By continuing, you are agreeing to{" "}
                <Box
                  component={Link}
                  to="/terms-conditions"
                  sx={{
                    color: "#FF5858",
                    fontWeight: 500,
                    cursor: "pointer",
                    textDecoration: "none",
                    display: "inline",
                    "&:hover": {
                      color: "#FF5858",
                      textDecoration: "none"
                    },
                    "&:active": {
                      color: "#FF5858",
                      textDecoration: "none"
                    },
                    "&:visited": {
                      color: "#FF5858",
                      textDecoration: "none"
                    },
                    "&:focus": {
                      color: "#FF5858",
                      textDecoration: "none"
                    }
                  }}
                >
                  Terms of Use
                </Box>{" "}
                and{" "}
                <Box
                  component={Link}
                  to="/privacy-policy"
                  sx={{
                    color: "#FF5858",
                    fontWeight: 500,
                    cursor: "pointer",
                    textDecoration: "none",
                    display: "inline",
                    "&:hover": {
                      color: "#FF5858",
                      textDecoration: "none"
                    },
                    "&:active": {
                      color: "#FF5858",
                      textDecoration: "none"
                    },
                    "&:visited": {
                      color: "#FF5858",
                      textDecoration: "none"
                    },
                    "&:focus": {
                      color: "#FF5858",
                      textDecoration: "none"
                    }
                  }}
                >
                  Privacy Policy
                </Box>
              </span>
              </Box>
              {errors.termsAccepted && (
                <Typography sx={{ color: "#F04951", fontSize: "12px", mt: 0.5, ml: 5 }}>
                  {errors.termsAccepted}
                </Typography>
              )}
            </Box>
            {/* Submit Button */}

            {/* ✅ Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={otpLoading || loading}
              style={{
                backgroundColor: "#F04951",
                color: "#FFFFFF",
                borderRadius: "10px",
                boxShadow: "0px 6px 0px #141414", // Increased from 4px to 6px for thicker shadow
                height: isMobile ? "36px" : "44px",
                width: isMobile ? "100%" : "160px",
                fontFamily: "Red Hat Display, sans-serif",
                fontWeight: 700,
                fontSize: isMobile ? "12px" : "14px",
                lineHeight: isMobile ? "11px" : "14px",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: isMobile ? "4px" : "8px",
                textTransform: "none",
                transition: "all 0.3s ease",
                cursor: (otpLoading || loading) ? "not-allowed" : "pointer",
                border: "none", // Add this to remove default button border
                opacity: (otpLoading || loading) ? 0.7 : 1,
              }}
            >
              {otpLoading ? "Sending OTP..." : loading ? "Submitting..." : "Submit"}
            </button>
            
            {/* Error Message */}
            {otpError && (
              <Typography
                sx={{
                  color: "#F04951",
                  fontSize: isMobile ? 12 : 14,
                  mt: 2,
                  textAlign: "center",
                }}
              >
                {otpError}
              </Typography>
            )}

            <VerificationCode
              open={showVerification}
              onClose={handleCloseVerification}
              email={emailForOTP}
              phoneNumber={emailForOTP}
              onVerify={handleVerifyCode}
              onResend={handleResendCode}
              onSuccess={handleSuccessPopupClose}
              isLoading={otpLoading}
              error={otpError}
            />

            <Dialog
              open={showRetryDialog}
              onClose={handleRetryReset}
              fullWidth
              maxWidth="xs"
            >
              <DialogTitle sx={{ fontWeight: 700, textAlign: "center" }}>
                Something went wrong
              </DialogTitle>
              <DialogContent>
                <Typography sx={{ textAlign: "center", fontSize: 14 }}>
                  We couldn&apos;t complete your request. Please try starting a new campaign again.
                </Typography>
              </DialogContent>
              <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleRetryReset}
                  sx={{
                    bgcolor: "#F04951",
                    textTransform: "none",
                    "&:hover": { bgcolor: "#E03E46" },
                  }}
                >
                  Try Again
                </Button>
              </DialogActions>
            </Dialog>

            {/* Decorative SVG or image: string-hand */}
            {!isMobile && ( // ✅ Hide when isMobile is true (<=600px)
              <Box
                sx={{
                  position: "absolute",
                  bottom: 20,
                  right: 0,
                  zIndex: 10,
                }}
              >
                <img
                  src={Images.stringHand}
                  alt="string hand"
                  style={{ width: 90, height: "auto" }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {!isMobile && <Box sx={{ mt: 4 }} />}
      {!isMobile && <NewsLogoSection />}
      <Box />

    </Box>
  );
};

export default StartNewCampaign;
